import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { useEffect, useState } from "react";

dayjs.extend(isSameOrAfter);

interface MonthRange {
  label: string;
  value: Dayjs;
  start: Dayjs;
  end: Dayjs;
  disabled: boolean;
}

const generateMonthsForYear = (
  year: number,
  minDate?: Dayjs,
  maxDate?: Dayjs,
): MonthRange[] => {
  return Array.from({ length: 12 }, (_, index) => {
    const value = dayjs().year(year).month(index).startOf("month");
    const start = value.startOf("month");
    const end = value.endOf("month");

    const now = dayjs();

    return {
      label: value.format("MMM"),
      value,
      start,
      end,
      disabled: end.isBefore(now.startOf("day")),
    };
  })
    .filter((v) => {
      return !minDate ? true : v.end.isAfter(minDate, "month");
    })
    .filter((v) => {
      return !maxDate ? true : v.start.isBefore(maxDate, "month");
    });
};

export const useMonthlyDates = ({
  initialDate,
  minDate,
  maxDate,
}: {
  initialDate: Dayjs;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}) => {
  const [currentYear, setCurrentYear] = useState(initialDate.year());
  const [selectedMonth, setSelectedMonth] = useState<MonthRange | null>(null);
  const [months, setMonths] = useState<MonthRange[]>([]);

  const now = dayjs();

  const goToPreviousYear = () => {
    setCurrentYear((prev) => prev - 1);
  };

  const goToNextYear = () => {
    if (currentYear < now.year()) {
      setCurrentYear((prev) => prev + 1);
    }
  };

  useEffect(() => {
    setMonths(generateMonthsForYear(currentYear, minDate, maxDate));
    // eslint-disable-next-line
  }, [currentYear]);

  return {
    currentYear,
    months,
    selectedMonth,
    setSelectedMonth,
    goToPreviousYear,
    goToNextYear,
    canGoPrev: currentYear > now.year() - 10,
    canGoNext: currentYear < now.year(),
  };
};
